/*
 * SPDX-FileCopyrightText: 2023 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

import { Observable } from 'rxjs';
import { delayWhen, filter, map, take, tap } from 'rxjs/operators';
import { ConfiguratorUtilsService } from './utils/configurator-utils.service';
import {ConfiguratorActions, ConfiguratorSelectors, StateWithConfigurator} from "../../state";
import {CommonConfiguratorUtilsService} from "../shared";
import {EntityLoaderState, StateUtils, UserIdService} from "@lm-nx-frontend/lm-inf-core";
import {CommonConfigurator} from "../core/model";
import {Configurator} from "../../../../../lm-inf-product-configurator-root/src/lib/model/configurator.model";
import {CheckoutQueryFacade} from "@lm-nx-frontend/lm-inf-checkout";
import {ActiveCartFacade} from "@lm-nx-frontend/lm-inf-cart";

@Injectable({ providedIn: 'root' })
export class ConfiguratorCartService {
  constructor(
    protected store: Store<StateWithConfigurator>,
    protected activeCartService: ActiveCartFacade,
    protected commonConfigUtilsService: CommonConfiguratorUtilsService,
    protected checkoutQueryFacade: CheckoutQueryFacade,
    protected userIdService: UserIdService,
    protected configuratorUtilsService: ConfiguratorUtilsService
  ) {}

  /**
   * Reads a configuratiom that is attached to a cart entry, dispatching the respective action
   * @param owner Configuration owner
   * @returns Observable of product configurations
   */
  readConfigurationForCartEntry(
    owner: CommonConfigurator.Owner
  ): Observable<Configurator.Configuration> {
    return this.store.pipe(
      select(
        ConfiguratorSelectors.getConfigurationProcessLoaderStateFactory(
          owner.key
        )
      ),
      //needed as we cannot read the cart in general and for the OV
      //in parallel, this can lead to cache issues with promotions
      delayWhen(() =>
        this.activeCartService.isStable().pipe(filter((stable) => stable))
      ),
      delayWhen(() =>
        this.checkoutQueryFacade.getCheckoutDetailsState().pipe(
          map((state) => state.loading),
          filter((loading) => !loading)
        )
      ),
      tap((configurationState) => {
        if (this.configurationNeedsReading(configurationState)) {
          this.activeCartService
            .requireLoadedCart()
            .pipe(take(1))
            .subscribe((cart) => {
              this.userIdService
                .getUserId()
                .pipe(take(1))
                .subscribe((userId) => {
                  const readFromCartEntryParameters: CommonConfigurator.ReadConfigurationFromCartEntryParameters =
                    {
                      userId: userId,
                      cartId: this.commonConfigUtilsService.getCartId(cart),
                      cartEntryNumber: owner.id,
                      owner: owner,
                    };
                  this.store.dispatch(
                    new ConfiguratorActions.ReadCartEntryConfiguration(
                      readFromCartEntryParameters
                    )
                  );
                });
            });
        }
      }),
      filter(
        (configurationState) =>
          configurationState.value !== undefined &&
          this.isConfigurationCreated(configurationState.value)
      ),
      //save to assume configuration is defined after previous filter
      map((configurationState) =>
        this.configuratorUtilsService.getConfigurationFromState(
          configurationState
        )
      )
    );
  }
  /**
   * Reads a configuratiom that is attached to an order entry, dispatching the respective action
   * @param owner Configuration owner
   * @returns Observable of product configurations
   */
  readConfigurationForOrderEntry(
    owner: CommonConfigurator.Owner
  ): Observable<Configurator.Configuration> {
    return this.store.pipe(
      select(
        ConfiguratorSelectors.getConfigurationProcessLoaderStateFactory(
          owner.key
        )
      ),
      tap((configurationState) => {
        if (this.configurationNeedsReading(configurationState)) {
          const ownerIdParts = this.commonConfigUtilsService.decomposeOwnerId(
            owner.id
          );
          const readFromOrderEntryParameters: CommonConfigurator.ReadConfigurationFromOrderEntryParameters =
            {
              userId: 'current', // OCC_USER_ID_CURRENT,
              orderId: ownerIdParts.documentId,
              orderEntryNumber: ownerIdParts.entryNumber,
              owner: owner,
            };
          this.store.dispatch(
            new ConfiguratorActions.ReadOrderEntryConfiguration(
              readFromOrderEntryParameters
            )
          );
        }
      }),
      filter(
        (configurationState) =>
          configurationState.value !== undefined &&
          this.isConfigurationCreated(configurationState.value)
      ),
      //save to assume configuration is defined after previous filter
      map((configurationState) =>
        this.configuratorUtilsService.getConfigurationFromState(
          configurationState
        )
      )
    );
  }

  /**
   * Adds a configuration to the cart, specified by the product code, a configuration ID and configuration owner key.
   *
   * @param productCode - Product code
   * @param configId - Configuration ID
   * @param owner Configuration owner
   */
  addToCart(
    productCode: string,
    configId: string,
    owner: CommonConfigurator.Owner
  ): void {
    this.activeCartService
      .requireLoadedCart()
      .pipe(take(1))
      .subscribe((cart) => {
        this.userIdService
          .getUserId()
          .pipe(take(1))
          .subscribe((userId) => {
            const addToCartParameters: Configurator.AddToCartParameters = {
              userId: userId,
              cartId: this.commonConfigUtilsService.getCartId(cart),
              productCode: productCode,
              quantity: 1,
              configId: configId,
              owner: owner,
              cartItem: {} as any
            };
            this.store.dispatch(
              new ConfiguratorActions.AddToCart(addToCartParameters)
            );
          });
      });
  }

  /**
   * Updates a cart entry, specified by the configuration.
   * The cart entry number for the entry that owns the configuration can be told
   * from the configuration's owner ID
   *
   * @param configuration - Configuration
   */
  updateCartEntry(configuration: Configurator.Configuration): void {
    this.activeCartService
      .requireLoadedCart()
      .pipe(take(1))
      .subscribe((cart) => {
        this.userIdService
          .getUserId()
          .pipe(take(1))
          .subscribe((userId) => {
            const parameters: Configurator.UpdateConfigurationForCartEntryParameters =
              {
                userId: userId,
                cartId: this.commonConfigUtilsService.getCartId(cart),
                cartEntryNumber: configuration.owner.id,
                configuration: configuration,
              };

            this.store.dispatch(
              new ConfiguratorActions.UpdateCartEntry(parameters)
            );
          });
      });
  }
  /**
   * Can be used to check if the active cart has any product configuration issues.
   * @returns True if and only if there is at least one cart entry with product configuration issues
   */
  activeCartHasIssues(): Observable<boolean> {
    return this.activeCartService.requireLoadedCart().pipe(
      map((cart) => {
        return cart ? cart.entries : [];
      }),
      map((entries) =>
        entries
          ? entries.filter((entry) =>
              this.commonConfigUtilsService.getNumberOfIssues(entry as any)
            )
          : []
      ),
      map((entries) => entries.length > 0)
    );
  }

  /**
   * Remove all configurations that are linked to cart entries
   */
  removeCartBoundConfigurations(): void {
    this.store.dispatch(
      new ConfiguratorActions.RemoveCartBoundConfigurations()
    );
  }

  protected isConfigurationCreated(
    configuration: Configurator.Configuration
  ): boolean {
    const configId: string = configuration.id;
    return configId.length !== 0;
  }

  protected configurationNeedsReading(
    configurationState: StateUtils.LoaderState<Configurator.Configuration>
  ): boolean {
    const configuration = configurationState.value;
    return (
      configuration === undefined ||
      (!this.isConfigurationCreated(configuration) &&
        !configurationState.loading &&
        !configurationState.error)
    );
  }
}
